<template>
  <div class="reporte-auditoria py-4">
    <div class="container-fluid">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <h2 class="mb-0 h5"><i class="bi bi-shield-lock me-2"></i>Auditoría</h2>
        <button class="btn btn-sm btn-outline-primary" @click="cargarDatos" :disabled="cargando">
          <span v-if="cargando" class="spinner-border spinner-border-sm me-1"></span>
          <i v-else class="bi bi-arrow-clockwise me-1"></i>Actualizar
        </button>
      </div>
      <p class="text-muted">Historial de acciones (crear, editar, eliminar, ingresos y salidas) de los usuarios de tu empresa.</p>

      <!-- Resumen -->
      <div class="row mb-3 g-2">
        <div class="col-6 col-md-3">
          <div class="card text-center h-100">
            <div class="card-body py-3">
              <h4 class="mb-0">{{ stats.eventos_hoy ?? 0 }}</h4>
              <small class="text-muted">Eventos hoy</small>
            </div>
          </div>
        </div>
        <div class="col-6 col-md-3">
          <div class="card text-center h-100">
            <div class="card-body py-3">
              <h4 class="mb-0">{{ stats.eventos_semana ?? 0 }}</h4>
              <small class="text-muted">Esta semana</small>
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="card h-100">
            <div class="card-body py-3">
              <small class="text-muted d-block mb-1">Módulos más usados (30 días)</small>
              <span v-if="!stats.top_modulos || stats.top_modulos.length === 0" class="text-muted">Sin datos aún.</span>
              <span v-for="m in stats.top_modulos" :key="m.modulo" class="badge bg-secondary me-1">
                {{ m.modulo }} ({{ m.total }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Filtros -->
      <div class="card mb-3">
        <div class="card-body">
          <div class="row g-2">
            <div class="col-md-3">
              <label class="form-label form-label-sm mb-1">Acción</label>
              <select class="form-select form-select-sm" v-model="filtros.accion" @change="cargarDatos">
                <option value="">Todas</option>
                <option v-for="a in acciones" :key="a" :value="a">{{ a }}</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label form-label-sm mb-1">Desde</label>
              <input type="date" class="form-control form-control-sm" v-model="filtros.date_from" @change="cargarDatos">
            </div>
            <div class="col-md-3">
              <label class="form-label form-label-sm mb-1">Hasta</label>
              <input type="date" class="form-control form-control-sm" v-model="filtros.date_to" @change="cargarDatos">
            </div>
          </div>
        </div>
      </div>

      <!-- Tabla -->
      <div class="card">
        <div class="table-responsive">
          <table class="table table-sm table-hover mb-0">
            <thead>
              <tr>
                <th>Fecha y hora</th>
                <th>Usuario</th>
                <th>Módulo / Submódulo</th>
                <th>Acción</th>
                <th>Descripción</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="cargando">
                <td colspan="6" class="text-center text-muted py-4">Cargando...</td>
              </tr>
              <tr v-else-if="logs.length === 0">
                <td colspan="6" class="text-center text-muted py-4">Sin eventos registrados para estos filtros.</td>
              </tr>
              <tr v-for="log in logs" :key="log.id" v-else>
                <td>{{ formatearFecha(log.fecha_evento) }}</td>
                <td>{{ log.usuario_nombre || ('#' + log.usuario_cliente) }}</td>
                <td>
                  <span v-if="log.modulo_nombre">{{ log.modulo_nombre }}<span v-if="log.submodulo_nombre"> / {{ log.submodulo_nombre }}</span></span>
                  <span v-else class="text-muted">—</span>
                </td>
                <td><span class="badge bg-secondary">{{ log.accion }}</span></td>
                <td>{{ log.descripcion }}</td>
                <td class="text-muted small">{{ log.ip_origen }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="card-body py-2" v-if="paginacion.total_pages > 1">
          <nav>
            <ul class="pagination pagination-sm justify-content-end mb-0">
              <li class="page-item" :class="{ disabled: paginacion.current_page <= 1 }">
                <a class="page-link" href="#" @click.prevent="irAPagina(paginacion.current_page - 1)">Anterior</a>
              </li>
              <li class="page-item disabled">
                <span class="page-link">Página {{ paginacion.current_page }} de {{ paginacion.total_pages }} ({{ paginacion.total }} eventos)</span>
              </li>
              <li class="page-item" :class="{ disabled: paginacion.current_page >= paginacion.total_pages }">
                <a class="page-link" href="#" @click.prevent="irAPagina(paginacion.current_page + 1)">Siguiente</a>
              </li>
            </ul>
          </nav>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import api from '@/services/api'

export default {
  name: 'ReporteAuditoria',
  setup() {
    const logs = ref([])
    const stats = ref({})
    const cargando = ref(false)
    const acciones = ['crear', 'actualizar', 'eliminar', 'login', 'logout', 'exportar', 'importar']

    const filtros = ref({
      accion: '',
      date_from: '',
      date_to: '',
    })

    const paginacion = ref({ current_page: 1, per_page: 50, total: 0, total_pages: 0 })

    const irAPagina = (pagina) => {
      if (pagina < 1 || pagina > paginacion.value.total_pages) return
      paginacion.value.current_page = pagina
      cargarDatos()
    }

    const cargarDatos = async () => {
      cargando.value = true
      try {
        const params = {
          ...filtros.value,
          page: paginacion.value.current_page,
          limit: paginacion.value.per_page,
        }
        const [logsRes, statsRes] = await Promise.all([
          api.get('/auditoria/mis-logs', { params }),
          api.get('/auditoria/mis-stats'),
        ])

        logs.value = logsRes.data?.data?.logs || []
        paginacion.value = logsRes.data?.data?.pagination || paginacion.value
        stats.value = statsRes.data?.data || {}
      } catch (error) {
        console.error('Error cargando auditoría:', error)
        logs.value = []
      } finally {
        cargando.value = false
      }
    }

    const formatearFecha = (fecha) => {
      if (!fecha) return '—'
      return new Date(fecha.replace(' ', 'T')).toLocaleString('es-EC')
    }

    onMounted(() => {
      cargarDatos()
    })

    return {
      logs,
      stats,
      cargando,
      acciones,
      filtros,
      paginacion,
      irAPagina,
      cargarDatos,
      formatearFecha,
    }
  },
}
</script>
